import React, { createContext, useContext, useState, useEffect } from 'react'
import { Sun, Moon, Monitor } from 'lucide-react'

export type ThemeMode = 'light' | 'dark' | 'system'

interface ThemeContextValue {
  themeMode: ThemeMode
  isDark: boolean
  setThemeMode: (mode: ThemeMode) => void
}

const STORAGE_KEY = 'poptavky_theme_mode_v1'

const ThemeContext = createContext<ThemeContextValue>({
  themeMode: 'system',
  isDark: false,
  setThemeMode: () => {},
})

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [themeMode, setThemeModeState] = useState<ThemeMode>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(STORAGE_KEY)
      if (saved === 'light' || saved === 'dark' || saved === 'system') {
        return saved
      }
    }
    return 'system'
  })

  const [systemDark, setSystemDark] = useState<boolean>(() => {
    if (typeof window !== 'undefined' && window.matchMedia) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches
    }
    return false
  })

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    const handler = (e: MediaQueryListEvent) => setSystemDark(e.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])

  const isDark = themeMode === 'dark' || (themeMode === 'system' && systemDark)

  useEffect(() => {
    if (typeof document === 'undefined') return
    const root = document.documentElement
    if (isDark) {
      root.classList.add('dark')
      root.dataset.theme = 'dark'
      root.style.colorScheme = 'dark'
    } else {
      root.classList.remove('dark')
      root.dataset.theme = 'light'
      root.style.colorScheme = 'light'
    }
  }, [isDark])

  const setThemeMode = (mode: ThemeMode) => {
    setThemeModeState(mode)
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, mode)
    }
  }

  return (
    <ThemeContext.Provider value={{ themeMode, isDark, setThemeMode }}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme() {
  return useContext(ThemeContext)
}

export interface ThemeSwitcherProps {
  compact?: boolean
  className?: string
}

export const ThemeSwitcher: React.FC<ThemeSwitcherProps> = ({
  compact = false,
  className = '',
}) => {
  const { themeMode, setThemeMode, isDark } = useTheme()

  const options: { id: ThemeMode; label: string; title: string; icon: React.ReactNode }[] = [
    {
      id: 'light',
      label: 'Světlý',
      title: 'Světlý režim',
      icon: <Sun className="w-3.5 h-3.5 shrink-0" />,
    },
    {
      id: 'dark',
      label: 'Tmavý',
      title: 'Tmavý režim',
      icon: <Moon className="w-3.5 h-3.5 shrink-0" />,
    },
    {
      id: 'system',
      label: 'Auto',
      title: 'Automaticky podle systému',
      icon: <Monitor className="w-3.5 h-3.5 shrink-0" />,
    },
  ]

  const toggleSingle = () => {
    setThemeMode(isDark ? 'light' : 'dark')
  }

  return (
    <>
      {/* Mobilní kompaktní jednotlačítkový přepínač (< sm) */}
      <button
        type="button"
        onClick={toggleSingle}
        title={isDark ? 'Přepnout na světlý režim' : 'Přepnout na tmavý režim'}
        aria-label={isDark ? 'Přepnout na světlý režim' : 'Přepnout na tmavý režim'}
        className={`sm:hidden inline-flex items-center justify-center w-8 h-8 rounded-xl bg-slate-100 dark:bg-slate-800 border border-slate-200/90 dark:border-slate-700 text-slate-700 dark:text-slate-200 shadow-2xs transition-all cursor-pointer active:scale-95 shrink-0 ${className}`}
      >
        {isDark ? (
          <Sun className="w-4 h-4 text-amber-400" />
        ) : (
          <Moon className="w-4 h-4 text-slate-700" />
        )}
      </button>

      {/* Desktopový plný 3-polohový přepínač (>= sm) */}
      <div
        role="group"
        aria-label="Nastavení vzhledu (Světlý / Tmavý / Auto)"
        className={`hidden sm:inline-flex items-center gap-1 p-1 rounded-xl bg-slate-100 dark:bg-slate-800/90 border border-slate-200/90 dark:border-slate-700/90 shadow-2xs ${className}`}
      >
        {options.map((opt) => {
          const active = themeMode === opt.id
          return (
            <button
              key={opt.id}
              type="button"
              onClick={() => setThemeMode(opt.id)}
              title={opt.title}
              aria-label={opt.title}
              aria-pressed={active}
              className={`inline-flex items-center justify-center gap-1.5 min-h-[34px] min-w-[34px] px-2.5 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer active:scale-95 ${
                active
                  ? opt.id === 'light'
                    ? 'bg-white dark:bg-slate-700 text-amber-700 dark:text-amber-300 shadow-xs ring-1 ring-slate-200/80 dark:ring-slate-600'
                    : opt.id === 'dark'
                    ? 'bg-slate-900 dark:bg-sky-500/20 text-sky-300 dark:text-sky-200 shadow-xs ring-1 ring-slate-700 dark:ring-sky-500/40'
                    : 'bg-white dark:bg-slate-700 text-emerald-700 dark:text-emerald-300 shadow-xs ring-1 ring-slate-200/80 dark:ring-slate-600'
                  : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white'
              }`}
            >
              {opt.icon}
              {!compact && <span className="hidden md:inline">{opt.label}</span>}
            </button>
          )
        })}
      </div>
    </>
  )
}
